import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HiOutlineCheckCircle, HiOutlineExclamation, HiOutlineSave, HiOutlineDocumentText } from 'react-icons/hi';
import { submitReview } from '../../../../store/slices/performanceReviewSlice';
import './ReviewForm.scss';

const ReviewForm = ({ report, onSubmitReview }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    feedback: '',
    score: 14
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  const { loading, error, success } = useSelector(state => state.performanceReview);
  
  useEffect(() => {
    if (report) {
      // Safely parse score - handle both string and number types
      const scoreValue = report.score ? parseInt(report.score, 10) : 14;
      setFormData({
        feedback: report.feedback || '',
        score: isNaN(scoreValue) ? 14 : scoreValue
      });
    }
  }, [report]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'score' ? parseInt(value, 10) : value
    });
  };
  
  const getScoreLabel = (score) => {
    if (score >= 18) return 'Outstanding';
    if (score >= 15) return 'Exceeds Expectations';
    if (score >= 10) return 'Meets Expectations';
    if (score >= 5) return 'Needs Improvement';
    return 'Unsatisfactory';
  };
  
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // Handle Firebase timestamp
    if (date.seconds) {
      date = new Date(date.seconds * 1000);
    }
    
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const getStatusInfo = () => {
    if (!report) return { text: 'No Report', icon: null, className: '' };
    
    if (report.reviewedAt) {
      return {
        text: 'Reviewed',
        icon: <HiOutlineCheckCircle className="icon" />,
        className: 'reviewed'
      };
    }
    
    if (report.submittedAt) {
      return {
        text: 'Pending Review',
        icon: <HiOutlineExclamation className="icon" />,
        className: 'pending'
      };
    }
    
    return {
      text: 'Not Submitted',
      icon: <HiOutlineDocumentText className="icon" />,
      className: 'blank'
    };
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!report || !formData.score) return;
    if (isSubmitting) return; // Prevent double submission
    
    setIsSubmitting(true);
    
    try {
      await dispatch(submitReview({
        reportId: report.id,
        reviewData: {
          feedback: formData.feedback,
          score: formData.score
        },
        employeeEmail: report.email,
        employeeName: report.employeeName || 'Employee',
        reviewerName: 'Admin',
        reviewerEmail: 'admin@myelinh.com'
      })).unwrap();
      
      // Show success modal
      setShowSuccessModal(true);
      
      // Call parent callback if provided
      if (onSubmitReview) {
        onSubmitReview({
          feedback: formData.feedback,
          score: formData.score
        });
      }
    } catch (error) {
      console.error('Review submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!report) {
    return (
      <div className="report-form-container">
        <div className="no-report-message">
          <HiOutlineDocumentText className="icon" />
          <p>Please select a report to review</p>
        </div>
      </div>
    );
  }
  
  const status = getStatusInfo();
  const isBlankReport = !report.submittedAt;
  
  return (
    <div className="report-form-container">
      <div className="report-header">
        <div className="date-range">
          <span className="label">Week of:</span>
          <span className="value">
            {formatDate(report.weekStartDate)} to {formatDate(report.weekEndDate)}
          </span>
        </div>
        <div className={`status ${status.className}`}>
          {status.icon && <span className="icon">{status.icon}</span>}
          <span>{status.text}</span>
        </div>
      </div>
      
      {isBlankReport ? (
        <div className="no-report-message">
          <p>This report has not been submitted yet</p>
          <p>The employee has not provided any data for this week's report</p>
        </div>
      ) : (
        <div className="report-form">
          <div className="form-group">
            <label htmlFor="objectives">Weekly Objectives</label>
            <textarea
              id="objectives"
              value={report.objectives || 'No objectives provided'}
              disabled
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="achievements">Key Achievements</label>
            <textarea
              id="achievements"
              value={report.measurableAchievements || 'No achievements provided'}
              disabled
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="proudAchievements">Proud Achievements</label>
              <textarea
                id="proudAchievements"
                value={report.proudAchievements || 'No achievements provided'}
                disabled
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="measurableResults">Measurable Results</label>
              <textarea
                id="measurableResults"
                value={report.measurableResults || 'No results provided'}
                disabled
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="improvements">Areas for Improvement</label>
            <textarea
              id="improvements"
              value={report.areasForImprovement || 'No improvement areas provided'}
              disabled
            />
          </div>
          
          <div className="form-group admin-review-section">
            <label htmlFor="score">Performance Score (1-20)</label>
            <div className="score-slider">
              <input
                type="range"
                id="score"
                name="score"
                min="1"
                max="20"
                value={formData.score}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              <div className="score-value-display">
                <div className="score-number">{formData.score}</div>
                <div className="score-text">{getScoreLabel(formData.score)}</div>
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="feedback">Feedback & Recommendations</label>
            <textarea
              id="feedback"
              name="feedback"
              placeholder="Provide feedback and recommendations to the employee..."
              value={formData.feedback}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="submit-button" 
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? (
                <>
                  <span className="loading-spinner"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <HiOutlineSave className="icon" />
                  {report.reviewedAt ? 'Update Review' : 'Submit Review'}
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-modal-header">
              <HiOutlineCheckCircle className="success-icon" />
              <h3>Review Submitted Successfully!</h3>
            </div>
            <div className="success-modal-content">
              <p>The performance review has been submitted with a score of <strong>{formData.score}</strong>.</p>
              <p>The employee will be notified via email and in-app notification.</p>
            </div>
            <div className="success-modal-actions">
              <button 
                className="success-modal-button"
                onClick={() => setShowSuccessModal(false)}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewForm; 